import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  MapPin, Shield, AlertTriangle, Clock, Users, Navigation,
  Phone, CheckCircle2, RefreshCw, Bell, Settings, XCircle,
  Timer, Car, Home, Building
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const GeofencingDashboard = () => {
  const [staffLocations, setStaffLocations] = useState([]);
  const [safetyAlerts, setSafetyAlerts] = useState([]);
  const [myStatus, setMyStatus] = useState(null);
  const [checkInHistory, setCheckInHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('map');
  const { toast } = useToast();

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Fetch geofencing data
  const fetchData = useCallback(async () => {
    try {
      const [locationsRes, alertsRes, statusRes] = await Promise.all([
        fetch(`${API}/api/phase45/geofencing/staff-locations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/phase45/geofencing/safety-alerts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/phase45/geofencing/my-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (locationsRes.ok) {
        const data = await locationsRes.json();
        setStaffLocations(data.locations || []);
      }
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setSafetyAlerts(data.alerts || []);
      }
      if (statusRes.ok) {
        const data = await statusRes.json();
        setMyStatus(data);
        setCheckInHistory(data.check_ins || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    // Poll for updates
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Manual check-in
  const checkIn = async (type = 'arrived') => {
    try {
      const res = await fetch(`${API}/api/phase45/geofencing/check-in`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: type,
          timestamp: new Date().toISOString()
        })
      });

      if (res.ok) {
        toast({
          title: "Checked In",
          description: type === 'arrived' ? "Arrival confirmed at visit location" : "Departure confirmed"
        });
        fetchData();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to check in", variant: "destructive" });
    }
  };

  // Trigger safety alert
  const triggerSafetyAlert = async () => {
    try {
      const res = await fetch(`${API}/api/phase45/geofencing/safety-alert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'manual',
          message: 'Staff member triggered safety alert'
        })
      });

      if (res.ok) {
        toast({
          title: "Safety Alert Sent",
          description: "Supervisors have been notified of your location",
          variant: "destructive"
        });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to send alert", variant: "destructive" });
    }
  };

  // Resolve safety alert
  const resolveAlert = async (alertId) => {
    try {
      const res = await fetch(`${API}/api/phase45/geofencing/alert/${alertId}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast({ title: "Alert Resolved", description: "Safety alert has been marked as resolved" });
        fetchData();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to resolve alert", variant: "destructive" });
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'at_visit': return 'bg-green-500';
      case 'in_transit': return 'bg-blue-500';
      case 'delayed': return 'bg-orange-500';
      case 'overdue': return 'bg-red-500';
      case 'off_duty': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'at_visit': return 'At Visit';
      case 'in_transit': return 'In Transit';
      case 'delayed': return 'Delayed';
      case 'overdue': return 'Overdue Check-in';
      case 'off_duty': return 'Off Duty';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className="h-7 w-7 text-teal-600" />
            Staff Safety & Geofencing
          </h1>
          <p className="text-gray-600">Real-time location tracking and safety management</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            className="bg-red-500 hover:bg-red-600"
            onClick={triggerSafetyAlert}
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Safety Alert
          </Button>
        </div>
      </div>

      {/* My Status Card */}
      <Card className={`border-2 ${
        myStatus?.status === 'overdue' ? 'border-red-300 bg-red-50' :
        myStatus?.status === 'at_visit' ? 'border-green-300 bg-green-50' : ''
      }`}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-4 h-4 rounded-full ${getStatusColor(myStatus?.status)}`} />
              <div>
                <h3 className="font-semibold">My Current Status</h3>
                <p className="text-sm text-gray-600">{getStatusLabel(myStatus?.status)}</p>
                {myStatus?.current_visit && (
                  <p className="text-xs text-gray-500">
                    At: {myStatus.current_visit.patient_name} • {myStatus.current_visit.address}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => checkIn('arrived')}
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Check In (Arrived)
              </Button>
              <Button 
                variant="outline"
                onClick={() => checkIn('departed')}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Navigation className="h-4 w-4 mr-2" />
                Check Out (Departed)
              </Button>
            </div>
          </div>
          
          {/* Timer for visit duration */}
          {myStatus?.status === 'at_visit' && myStatus?.check_in_time && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
              <Timer className="h-4 w-4" />
              <span>Visit duration: {myStatus.visit_duration || '0'} minutes</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Safety Alerts */}
      {safetyAlerts.filter(a => !a.resolved).length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-red-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Active Safety Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {safetyAlerts.filter(a => !a.resolved).map((alert, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <div>
                      <h4 className="font-medium text-red-900">{alert.staff_name}</h4>
                      <p className="text-sm text-red-700">{alert.message}</p>
                      <p className="text-xs text-red-600">
                        {new Date(alert.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      <Phone className="h-4 w-4 mr-1" />
                      Call
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-green-500 hover:bg-green-600"
                      onClick={() => resolveAlert(alert.id)}
                    >
                      Resolve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Staff on Field</p>
                <p className="text-2xl font-bold">{staffLocations.filter(s => s.status !== 'off_duty').length}</p>
              </div>
              <Car className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">At Visits</p>
                <p className="text-2xl font-bold">{staffLocations.filter(s => s.status === 'at_visit').length}</p>
              </div>
              <Home className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue Check-ins</p>
                <p className="text-2xl font-bold text-red-600">
                  {staffLocations.filter(s => s.status === 'overdue').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Alerts</p>
                <p className="text-2xl font-bold text-orange-600">
                  {safetyAlerts.filter(a => !a.resolved).length}
                </p>
              </div>
              <Bell className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Locations Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-teal-600" />
            Team Locations
          </CardTitle>
          <CardDescription>Real-time staff status and location tracking</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {staffLocations.length > 0 ? (
              staffLocations.map((staff, i) => (
                <div 
                  key={i}
                  className={`flex items-center justify-between p-4 border rounded-lg ${
                    staff.status === 'overdue' ? 'border-red-200 bg-red-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(staff.status)}`} />
                    <div>
                      <h4 className="font-medium">{staff.name}</h4>
                      <p className="text-sm text-gray-600">{staff.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <Badge variant="outline">{getStatusLabel(staff.status)}</Badge>
                      {staff.current_patient && (
                        <p className="text-xs text-gray-500 mt-1">
                          Patient: {staff.current_patient}
                        </p>
                      )}
                    </div>
                    <div className="text-right text-sm">
                      {staff.last_check_in && (
                        <p className="text-gray-500">
                          Last check-in: {new Date(staff.last_check_in).toLocaleTimeString()}
                        </p>
                      )}
                      {staff.eta && (
                        <p className="text-blue-600">ETA: {staff.eta}</p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm">
                      <Phone className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No staff locations available</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* My Check-in History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">My Check-in History</CardTitle>
          <CardDescription>Today's visit check-ins and check-outs</CardDescription>
        </CardHeader>
        <CardContent>
          {checkInHistory.length > 0 ? (
            <div className="space-y-2">
              {checkInHistory.map((checkIn, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {checkIn.type === 'arrived' ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <Navigation className="h-5 w-5 text-blue-500" />
                    )}
                    <div>
                      <p className="font-medium">
                        {checkIn.type === 'arrived' ? 'Arrived' : 'Departed'}
                      </p>
                      <p className="text-sm text-gray-600">{checkIn.patient_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">
                      {new Date(checkIn.timestamp).toLocaleTimeString()}
                    </p>
                    {checkIn.duration && (
                      <p className="text-xs text-gray-500">
                        Duration: {checkIn.duration} min
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No check-ins today</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GeofencingDashboard;
